// pages/billing/DisputifierInvoices.jsx — SOUND-447 (Billing epic, Phase C)
//
// Internal-only centralized AR view over the Disputifier billing provider.
// Backend: GET /billing/disputifier{,/summary} (Phase B, over soundApp.billingInvoices).
//
// The React CRM version of the standalone tool at /Sound/disputifier-invoices/,
// with two net-new capabilities: row selection + "Export selected" CSV.
//
// IMPORTANT: Disputifier amounts are WHOLE DOLLARS — never /100.
// midId / dueDate are null (Disputifier doesn't emit them) — don't render them.
import React, { useState, useMemo, useEffect } from "react";
import { requireAuth } from "utilities/auth";
import { useDisputifierInvoices } from "hooks/crm/useDisputifierInvoices";
import PageHeader from "components/PageLayout";
import PageKpis from "components/PageKpis";
import EmptyState from "components/EmptyState";
import Skeleton from "components/Skeleton";
import { useToast } from "components/Toast";
import {
  DollarSign,
  CheckCircle,
  Clock,
  Copy,
  Download,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ExternalLink,
  Info,
  Receipt,
} from "lucide-react";

// ---------- formatting ----------
const usd2 = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n || 0));

function ageDays(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / 86400000);
}

function formatDate(iso) {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "—";
  return new Date(t).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ---------- small presentational pieces ----------
const STATUS_STYLES = {
  paid: "bg-success/15 text-success",
  open: "bg-warning/15 text-warning",
  draft: "bg-info/15 text-info",
  uncollectible: "bg-destructive/15 text-destructive",
  void: "bg-muted text-muted-foreground",
};

function StatusPill({ invoice }) {
  const status = invoice.paid ? "paid" : invoice.status || "open";
  const style = STATUS_STYLES[status] || "bg-muted text-muted-foreground";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide ${style}`}
    >
      {status}
    </span>
  );
}

function AgeCell({ iso, paid }) {
  const age = ageDays(iso);
  if (age === null) return <span className="text-muted-foreground">—</span>;
  // Paid rows are shown neutral-green; open rows color by overdue threshold.
  const cls = paid
    ? "text-success"
    : age > 30
    ? "text-destructive font-medium"
    : age < 7
    ? "text-success"
    : "text-muted-foreground";
  return <span className={`text-xs ${cls}`}>{age}d</span>;
}

function SortHeader({ label, colKey, sortBy, sortDir, onSort, align = "left" }) {
  const active = sortBy === colKey;
  return (
    <th
      className={`px-4 py-3 text-${align} text-xs font-medium text-muted-foreground uppercase tracking-wider select-none ${
        onSort ? "cursor-pointer" : ""
      }`}
      onClick={onSort ? () => onSort(colKey) : undefined}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active &&
          (sortDir === "asc" ? (
            <ChevronUp className="h-3 w-3 text-brand" />
          ) : (
            <ChevronDown className="h-3 w-3 text-brand" />
          ))}
      </span>
    </th>
  );
}

// ---------- CSV export (extends the BinAnalysis.jsx pattern) ----------
const csvEscape = (val) => {
  if (val === null || val === undefined) return "";
  const s = String(val);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function exportInvoicesCsv(rows) {
  const headers = [
    "merchant",
    "invoice_number",
    "status",
    "amount_due",
    "amount_remaining",
    "invoice_date",
    "hosted_invoice_url",
  ];
  const body = rows.map((inv) => [
    csvEscape(inv.merchantName),
    csvEscape(inv.providerInvoiceId),
    csvEscape(inv.paid ? "paid" : inv.status || "open"),
    Number(inv.amountDue ?? 0).toFixed(2),
    Number(inv.amountRemaining ?? 0).toFixed(2),
    csvEscape(inv.invoiceDate ? inv.invoiceDate.slice(0, 10) : ""),
    csvEscape(inv.hostedInvoiceUrl || ""),
  ]);
  const csv = [headers.join(","), ...body.map((r) => r.join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `disputifier-invoices_${new Date()
    .toISOString()
    .slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------- shared invoice badge ----------
function SharedBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 badge badge-muted text-[10px] uppercase"
      title="Same invoice appears under more than one merchant — counted once in Outstanding to avoid double-counting."
    >
      <Copy className="h-3 w-3" />
      shared
    </span>
  );
}

const PAGE_LIMIT = 100;

function DisputifierInvoicesInner() {
  const { toast } = useToast();

  // filters
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [merchant, setMerchant] = useState("all");
  const [groupByMerchant, setGroupByMerchant] = useState(true);

  // sort (server-side)
  const [sortBy, setSortBy] = useState("age");
  const [sortDir, setSortDir] = useState("desc");

  // pagination
  const [page, setPage] = useState(1);

  // row selection (net-new): a Set of providerInvoiceId
  const [selected, setSelected] = useState(() => new Set());

  const filters = useMemo(
    () => ({ status, search, merchant }),
    [status, search, merchant]
  );

  // Reset to page 1 whenever filters/sort change.
  const prevKey = React.useRef("");
  useEffect(() => {
    const key = `${status}|${search}|${merchant}|${sortBy}|${sortDir}`;
    if (prevKey.current && prevKey.current !== key) setPage(1);
    prevKey.current = key;
  }, [status, search, merchant, sortBy, sortDir]);

  const {
    invoices,
    pagination,
    summary,
    merchants,
    loading,
    summaryLoading,
    error,
  } = useDisputifierInvoices({
    filters,
    page,
    limit: PAGE_LIMIT,
    sortBy,
    sortDir,
  });

  const handleSort = (colKey) => {
    if (colKey === sortBy) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(colKey);
      setSortDir("desc");
    }
  };

  // Merchant filter options derived from the current page. (A dedicated
  // /merchants endpoint could back a global list later; page-derived is
  // sufficient for V1 and matches the standalone tool.)
  // Global merchant list from the API (whole collection) so the filter lists
  // EVERY merchant, not just those on the current page. value=id, label=name.
  const merchantOptions = useMemo(
    () =>
      merchants
        .map((m) => ({
          id: m.providerMerchantId,
          name: m.merchantName || m.providerMerchantId,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [merchants]
  );

  // ----- selection helpers -----
  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const pageIds = useMemo(
    () => invoices.map((i) => i.providerInvoiceId).filter(Boolean),
    [invoices]
  );
  const allSelectedOnPage =
    pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelectedOnPage) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleExportSelected = () => {
    const rows = invoices.filter((i) => selected.has(i.providerInvoiceId));
    if (rows.length === 0) {
      toast({
        status: "warning",
        title: "No invoices selected",
        description: "Select one or more rows, then export.",
      });
      return;
    }
    exportInvoicesCsv(rows);
    toast({
      status: "success",
      title: "Export ready",
      description: `${rows.length} invoice${rows.length === 1 ? "" : "s"} exported.`,
    });
  };

  // ----- grouped view model -----
  const groups = useMemo(() => {
    if (!groupByMerchant) return null;
    const map = new Map();
    for (const inv of invoices) {
      const name = inv.merchantName || "—";
      if (!map.has(name)) {
        map.set(name, {
          name,
          url: inv.merchantUrl,
          invoices: [],
          remaining: 0,
          openCount: 0,
        });
      }
      const g = map.get(name);
      g.invoices.push(inv);
      // De-dupe shared invoices when subtotaling outstanding: only count a
      // shared invoice's remaining once per merchant group.
      if (inv.status === "open") {
        g.remaining += Number(inv.amountRemaining || inv.amountDue || 0);
        g.openCount++;
      }
    }
    return [...map.values()].sort((a, b) => {
      if (a.remaining !== b.remaining) return b.remaining - a.remaining;
      return a.name.localeCompare(b.name);
    });
  }, [groupByMerchant, invoices]);

  // ----- KPI items from /summary -----
  const kpiItems = useMemo(() => {
    const s = summary || {};
    return [
      {
        label: "Outstanding",
        value: s.outstanding ?? 0,
        format: "currency",
        tone: (s.outstanding ?? 0) > 0 ? "destructive" : "neutral",
        icon: <DollarSign />,
        loading: summaryLoading,
      },
      {
        label: "Paid all-time",
        value: s.paidAllTime ?? 0,
        format: "currency",
        tone: "success",
        icon: <CheckCircle />,
        loading: summaryLoading,
      },
      {
        label: "Oldest open",
        value: s.oldestOpenAgeDays ?? 0,
        tone: (s.oldestOpenAgeDays ?? 0) > 30 ? "warning" : "neutral",
        icon: <Clock />,
        loading: summaryLoading,
      },
      {
        label: "Shared invoices",
        value: s.sharedInvoiceCount ?? 0,
        tone: "neutral",
        icon: <Copy />,
        loading: summaryLoading,
      },
    ];
  }, [summary, summaryLoading]);

  const selectedCount = selected.size;

  // ----- a single invoice <tr> -----
  const renderInvoiceRow = (inv, indented) => {
    const id = inv.providerInvoiceId;
    const checked = selected.has(id);
    return (
      <tr
        key={`${inv.providerMerchantId}:${id}`}
        className={`hover:bg-muted transition-colors ${
          checked ? "bg-brand/5" : ""
        }`}
      >
        <td className="px-4 py-3">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-border accent-brand"
            checked={checked}
            onChange={() => toggleRow(id)}
            aria-label={`Select invoice ${id}`}
          />
        </td>
        <td className="px-4 py-3">
          {indented ? (
            <span className="text-muted-foreground text-xs pl-4">↳</span>
          ) : inv.merchantUrl ? (
            <a
              href={`https://${inv.merchantUrl.replace(/^https?:\/\//, "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-info hover:underline text-sm"
              onClick={(e) => e.stopPropagation()}
            >
              {inv.merchantName}
            </a>
          ) : (
            <span className="text-sm text-foreground">{inv.merchantName}</span>
          )}
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-muted-foreground">
              {inv.providerInvoiceId || "—"}
            </span>
            {inv.sharedInvoiceAcrossMerchants && <SharedBadge />}
          </div>
        </td>
        <td className="px-4 py-3">
          <StatusPill invoice={inv} />
        </td>
        <td className="px-4 py-3 text-right text-sm tabular-nums text-foreground">
          {usd2(inv.amountDue)}
        </td>
        <td className="px-4 py-3 text-right text-sm tabular-nums text-foreground">
          {usd2(inv.amountRemaining)}
        </td>
        <td className="hidden md:table-cell px-4 py-3 text-sm text-muted-foreground">
          {formatDate(inv.invoiceDate)}
        </td>
        <td className="px-4 py-3 text-right">
          <AgeCell iso={inv.invoiceDate} paid={inv.paid} />
        </td>
        <td className="px-4 py-3 text-right">
          {inv.hostedInvoiceUrl ? (
            <a
              href={inv.hostedInvoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-info hover:underline text-xs"
              onClick={(e) => e.stopPropagation()}
            >
              view <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div>
      <PageHeader
        title="Disputifier Invoices"
        subtitle="Outstanding and paid invoices synced from Disputifier billing."
        icon={<Receipt className="h-6 w-6" />}
        meta={[
          { label: "Total", value: (pagination.total || 0).toLocaleString() },
          {
            label: "Merchants",
            value: (summary?.merchantsWithInvoices ?? "—").toString(),
          },
        ]}
        actions={
          <button
            className="btn btn-primary"
            onClick={handleExportSelected}
            disabled={selectedCount === 0}
            title={
              selectedCount === 0
                ? "Select rows to export"
                : `Export ${selectedCount} selected`
            }
          >
            <Download className="h-4 w-4" />
            Export selected{selectedCount > 0 ? ` (${selectedCount})` : ""}
          </button>
        }
      />

      <div className="container py-6 space-y-6">
        {/* KPIs */}
        <PageKpis items={kpiItems} columns={{ base: 1, md: 2, lg: 4 }} />

        {/* Status caveat legend */}
        <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 border border-border rounded-lg px-3 py-2">
          <Info className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Status is <strong>per the Disputifier API</strong> and may lag actual
            payment by 3 to 5 days — reconcile against the Disputifier billing
            export before chasing.
          </span>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-2 surface rounded-lg p-3">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search merchant or invoice #…"
            className="input h-9 min-w-[240px] flex-1"
          />
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="select h-9 w-auto"
          >
            <option value="all">All statuses</option>
            <option value="open">Open</option>
            <option value="paid">Paid</option>
            <option value="draft">Draft</option>
            <option value="uncollectible">Uncollectible</option>
            <option value="void">Void</option>
          </select>
          <select
            value={merchant}
            onChange={(e) => setMerchant(e.target.value)}
            className="select h-9 w-auto"
          >
            <option value="all">All merchants</option>
            {merchantOptions.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          <label className="inline-flex items-center gap-2 text-sm text-foreground cursor-pointer select-none px-2">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-border accent-brand"
              checked={groupByMerchant}
              onChange={(e) => setGroupByMerchant(e.target.checked)}
            />
            Group by merchant
          </label>
        </div>

        {error && (
          <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2">
            Failed to load invoices. The Billing API (Phase B) may not be
            deployed yet.
          </div>
        )}

        {/* Table */}
        <div className="bg-card rounded-2xl shadow border border-border">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-card">
                <tr>
                  <th className="px-4 py-3 text-left w-10">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-border accent-brand"
                      checked={allSelectedOnPage}
                      onChange={toggleSelectAll}
                      aria-label="Select all on page"
                    />
                  </th>
                  <SortHeader
                    label="Merchant"
                    colKey="merchant"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={groupByMerchant ? undefined : handleSort}
                  />
                  <SortHeader
                    label="Invoice #"
                    colKey="invoice"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortHeader
                    label="Status"
                    colKey="status"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortHeader
                    label="Amount"
                    colKey="amountDue"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleSort}
                    align="right"
                  />
                  <SortHeader
                    label="Remaining"
                    colKey="amountRemaining"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleSort}
                    align="right"
                  />
                  <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    <span
                      className="inline-flex items-center gap-1 cursor-pointer select-none"
                      onClick={() => handleSort("invoiceDate")}
                    >
                      Invoice Date
                      {sortBy === "invoiceDate" &&
                        (sortDir === "asc" ? (
                          <ChevronUp className="h-3 w-3 text-brand" />
                        ) : (
                          <ChevronDown className="h-3 w-3 text-brand" />
                        ))}
                    </span>
                  </th>
                  <SortHeader
                    label="Age"
                    colKey="age"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleSort}
                    align="right"
                  />
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Link
                  </th>
                </tr>
              </thead>

              {loading ? (
                <tbody className="divide-y divide-border">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <tr key={i}>
                      <td className="px-4 py-4" colSpan={9}>
                        <Skeleton.Rect width="100%" height={16} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              ) : groupByMerchant && groups ? (
                <tbody className="divide-y divide-border">
                  {groups.map((g) => (
                    <React.Fragment key={g.name}>
                      <tr className="bg-muted/50">
                        <td className="px-4 py-2" colSpan={9}>
                          <div className="flex items-center justify-between gap-3">
                            <span className="flex items-baseline gap-2">
                              {g.url ? (
                                <a
                                  href={`https://${g.url.replace(
                                    /^https?:\/\//,
                                    ""
                                  )}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-semibold text-foreground hover:underline"
                                >
                                  {g.name}
                                </a>
                              ) : (
                                <span className="font-semibold text-foreground">
                                  {g.name}
                                </span>
                              )}
                              <span className="text-xs text-muted-foreground">
                                {g.invoices.length} invoice
                                {g.invoices.length === 1 ? "" : "s"}
                                {g.openCount > 0 && ` · ${g.openCount} open`}
                              </span>
                            </span>
                            {g.remaining > 0 && (
                              <span className="text-sm font-semibold tabular-nums text-destructive">
                                {usd2(g.remaining)} outstanding
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                      {g.invoices.map((inv) => renderInvoiceRow(inv, true))}
                    </React.Fragment>
                  ))}
                </tbody>
              ) : (
                <tbody className="divide-y divide-border">
                  {invoices.map((inv) => renderInvoiceRow(inv, false))}
                </tbody>
              )}
            </table>
          </div>

          {!loading && invoices.length === 0 && (
            <EmptyState
              icon={Receipt}
              heading="No invoices found"
              description={
                status !== "all" || search || merchant !== "all"
                  ? "Try adjusting your filters."
                  : "No Disputifier invoices have synced yet."
              }
            />
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="btn btn-ghost h-9 w-9 p-0 disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm text-muted-foreground px-3">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() =>
                setPage((p) => Math.min(pagination.totalPages, p + 1))
              }
              disabled={page >= pagination.totalPages}
              className="btn btn-ghost h-9 w-9 p-0 disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default requireAuth(DisputifierInvoicesInner);
